// Progress of "Confirmă alergenii" over menus of hundreds of products (admin review 2026-11-02): which products are
// confirmed, which were skipped for now, where to resume. Kept per restaurant and menu in the browser, so leaving
// the page (or the phone locking) never loses the place. Confirmation itself is only ever the server's: one
// product at a time, never collectively.

export interface ProgressItem {
  id: string;
  categoryName: string;
  confirmed: boolean;
}

export interface SavedProgress {
  /** Product the restaurant was looking at. */
  at: string | null;
  /** Skipped for now ("Omit pentru moment"). */
  skipped: string[];
}

export const EMPTY_PROGRESS: SavedProgress = { at: null, skipped: [] };

const key = (rid: string, menuId: string) => `mnv:allergens:${rid}:${menuId}`;

export function loadProgress(rid: string, menuId: string): SavedProgress {
  try {
    const raw = localStorage.getItem(key(rid, menuId));
    if (!raw) return EMPTY_PROGRESS;
    const v = JSON.parse(raw) as Partial<SavedProgress>;
    return {
      at: typeof v.at === "string" ? v.at : null,
      skipped: Array.isArray(v.skipped) ? v.skipped.filter((x) => typeof x === "string") : [],
    };
  } catch {
    return EMPTY_PROGRESS;
  }
}

export function saveProgress(rid: string, menuId: string, p: SavedProgress) {
  try {
    localStorage.setItem(key(rid, menuId), JSON.stringify(p));
  } catch {
    // private mode: progress lives only for this visit
  }
}

/** Products of the chosen category (all when null), in the server's order (unconfirmed first). */
export function inCategory<T extends ProgressItem>(items: T[], category: string | null): T[] {
  return category ? items.filter((x) => x.categoryName === category) : items;
}

export function counts(items: ProgressItem[], done: ReadonlySet<string>, skipped: ReadonlySet<string>) {
  const confirmed = items.filter((x) => x.confirmed || done.has(x.id)).length;
  const skippedN = items.filter((x) => !x.confirmed && !done.has(x.id) && skipped.has(x.id)).length;
  return { total: items.length, confirmed, skipped: skippedN, left: items.length - confirmed - skippedN };
}

/**
 * Where to resume: the saved product if it is still in the list, else the first one neither confirmed nor
 * skipped, else the first skipped one, else the end (everything confirmed).
 */
export function resumeIndex(
  items: ProgressItem[],
  saved: SavedProgress,
  done: ReadonlySet<string> = new Set(),
): number {
  const open = (x: ProgressItem) => !x.confirmed && !done.has(x.id);
  if (saved.at) {
    const i = items.findIndex((x) => x.id === saved.at);
    if (i >= 0 && open(items[i] as ProgressItem)) return i;
  }
  const skipped = new Set(saved.skipped);
  const next = items.findIndex((x) => open(x) && !skipped.has(x.id));
  if (next >= 0) return next;
  const firstSkipped = items.findIndex(open);
  return firstSkipped >= 0 ? firstSkipped : items.length;
}

/** After confirming or skipping the product at `i`: the next one still open (not confirmed, not skipped). */
export function nextOpen(
  items: ProgressItem[],
  i: number,
  done: ReadonlySet<string>,
  skipped: ReadonlySet<string>,
): number {
  for (let k = i + 1; k < items.length; k++) {
    const x = items[k] as ProgressItem;
    if (!x.confirmed && !done.has(x.id) && !skipped.has(x.id)) return k;
  }
  return items.length;
}
